import { useEffect, useRef, useState } from 'react'
import type { EliteOnboardingStatusResponse, ElitePlanOfferType } from '../../types/misc.types'
import RenderHTML from '../ui-components/RenderHTML'
import { convertEpochToDate } from '../../utils/utils'

export type EliteSpecialOfferGiftBannerVariant = 'TENURE_BASED' | 'PRICE_BASED'

const GIFT_BANNER_IMAGES: Record<EliteSpecialOfferGiftBannerVariant, string> = {
  TENURE_BASED: '/images/landing_page_gift.png',
  PRICE_BASED: '/images/landing_page_gift_discounted.png'
}

const OFFER_TYPE_GIFT_BANNER_IMAGES: Partial<Record<ElitePlanOfferType, string>> = {
  INDEPENDENCE_DAY_OFFER_EXTENDED_TIME: '/images/landing_page_gift_independence.png',
  INDEPENDENCE_DAY_OFFER_DISCOUNTED_PRICE: '/images/landing_page_gift_discounted_independence.png',
  SIGNUP_TENURE_BASED_OFFER: '/images/landing_page_gift_signup_tenure.png',
  TWELVE_PLUS_THREE: '/images/landing_page_plus_three_offer.png',
  FESTIVE_BASED: '/images/landing_page_two_months.png'
}

const TWELVE_PLUS_THREE_EXTRA_MONTHS = 3

const ONE_DAY_MS = 24 * 60 * 60 * 1000

export function isTenureBasedOfferType(offerType?: ElitePlanOfferType | null): boolean {
  return (
    offerType === 'TENURE_BASED' ||
    offerType === 'FESTIVE_BASED' ||
    offerType === 'INDEPENDENCE_DAY_OFFER_EXTENDED_TIME' ||
    offerType === 'SIGNUP_TENURE_BASED_OFFER' ||
    offerType === 'TWELVE_PLUS_THREE'
  )
}

export function isTwelvePlusThreeOffer(offerType?: ElitePlanOfferType | null): boolean {
  return offerType === 'TWELVE_PLUS_THREE'
}

export function isPriceBasedOfferType(offerType?: ElitePlanOfferType | null): boolean {
  return offerType === 'PRICE_BASED' || offerType === 'INDEPENDENCE_DAY_OFFER_DISCOUNTED_PRICE'
}

export function isSignupTenureBasedOffer(
  offerType?: ElitePlanOfferType | null,
  signupOffer?: boolean | null
): boolean {
  return signupOffer === true || offerType === 'SIGNUP_TENURE_BASED_OFFER'
}

export function resolveEliteLandingOfferFromStatus(status?: EliteOnboardingStatusResponse | null) {
  const user = status?.userSubscriptionDetails
  const plans = status?.eligiblePlans ?? []
  const signupPlan = plans.find((plan) => isSignupTenureBasedOffer(plan?.offerType, plan?.signupOffer))
  const plan = signupPlan ?? plans.find((item) => item?.recommended) ?? plans[0]

  return {
    specialOffering: user?.specialOffering ?? plan?.specialOffering ?? false,
    signupOffer: user?.signupOffer === true || plan?.signupOffer === true,
    offerType: user?.offerType ?? plan?.offerType ?? null,
    offerStartTime: user?.offerStartTime ?? plan?.offerStartTime ?? null,
    offerExpiryTime: user?.offerExpiryTime ?? plan?.offerValidTill ?? null
  }
}

export function resolveSpecialOfferGiftBannerVariant(
  offerType?: ElitePlanOfferType | null,
  membershipFees?: number
): EliteSpecialOfferGiftBannerVariant {
  if (isTenureBasedOfferType(offerType)) return 'TENURE_BASED'
  if (isPriceBasedOfferType(offerType)) return 'PRICE_BASED'
  if (membershipFees !== undefined && membershipFees === 499) return 'PRICE_BASED'
  return 'TENURE_BASED'
}

export function resolveSpecialOfferGiftBannerImage(
  offerType?: ElitePlanOfferType | null,
  variant?: EliteSpecialOfferGiftBannerVariant,
  signupOffer?: boolean | null
): string {
  if (isSignupTenureBasedOffer(offerType, signupOffer)) {
    return OFFER_TYPE_GIFT_BANNER_IMAGES.SIGNUP_TENURE_BASED_OFFER ?? GIFT_BANNER_IMAGES.TENURE_BASED
  }
  const offerTypeImage = offerType ? OFFER_TYPE_GIFT_BANNER_IMAGES[offerType] : undefined
  if (offerTypeImage) return offerTypeImage
  return GIFT_BANNER_IMAGES[variant ?? 'TENURE_BASED']
}

export function isExplicitEliteOfferType(offerType?: ElitePlanOfferType | null): boolean {
  if (!offerType) return false
  return offerType !== 'NO_OFFER'
}

export function toOfferTimestampMs(timestamp?: number | null): number | null {
  if (timestamp == null) return null
  return timestamp < 1e12 ? timestamp * 1000 : timestamp
}

export function isEliteOfferTimeWindowActive(
  offerStartTime?: number | null,
  offerExpiryTime?: number | null
): boolean {
  const startTimestampMs = toOfferTimestampMs(offerStartTime)
  const expiryTimestampMs = toOfferTimestampMs(offerExpiryTime)
  if (startTimestampMs == null || expiryTimestampMs == null) return false
  return Date.now() >= startTimestampMs && Date.now() <= expiryTimestampMs
}

export function getOfferRemainingMs(offerValidTill?: number | null): number {
  const expiryTimestampMs = toOfferTimestampMs(offerValidTill)
  if (expiryTimestampMs == null) return 0
  return Math.max(0, expiryTimestampMs - Date.now())
}

export function isOfferLessThanOneDayRemaining(offerValidTill?: number | null): boolean {
  const remainingMs = getOfferRemainingMs(offerValidTill)
  return remainingMs > 0 && remainingMs < ONE_DAY_MS
}

export function formatOfferCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const hh = String(hours).padStart(2, '0')
  const mm = String(minutes).padStart(2, '0')
  const ss = String(seconds).padStart(2, '0')
  return `${hh}:${mm}:${ss}`
}

export function shouldShowEliteOfferBanner({
  offerType,
  specialOffering,
  signupOffer,
  offerStartTime,
  offerExpiryTime
}: {
  offerType?: ElitePlanOfferType | null
  specialOffering?: boolean | null
  signupOffer?: boolean | null
  offerStartTime?: number | null
  offerExpiryTime?: number | null
}): boolean {
  if (isSignupTenureBasedOffer(offerType, signupOffer)) {
    if (offerStartTime == null || offerExpiryTime == null) return true
    return isEliteOfferTimeWindowActive(offerStartTime, offerExpiryTime)
  }
  if (offerType === 'NO_OFFER') return false
  if (isExplicitEliteOfferType(offerType)) {
    if (offerStartTime == null || offerExpiryTime == null) return true
    return isEliteOfferTimeWindowActive(offerStartTime, offerExpiryTime)
  }
  if (specialOffering === false) return false
  return isEliteOfferTimeWindowActive(offerStartTime, offerExpiryTime)
}

const PRICE_BASED_COPY = 'Lowest price, unlocked just for you'

const shineIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="176" height="75" viewBox="0 0 176 75" fill="none">
  <g opacity="0.7" filter="url(#filter0_f_39936_7724)">
    <path d="M103.094 -8.44769H164.918L72.9579 123.512L11.1345 123.512L103.094 -8.44769Z" fill="white"/>
  </g>
  <defs>
    <filter id="filter0_f_39936_7724" x="0.134521" y="-19.4478" width="175.783" height="153.96" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
      <feGaussianBlur stdDeviation="5.5" result="effect1_foregroundBlur_39936_7724"/>
    </filter>
  </defs>
</svg>`

const shineKeyframesCss = `
  @keyframes elite-special-offer-shine {
    0% {
      transform: translateX(-100%) scale(1.25);
      opacity: 1;
    }
    40% {
      transform: translateX(-20%) scale(1.25);
      opacity: 1;
    }
    100% {
      transform: translateX(100%) scale(1.25);
      opacity: 0;
    }
  }
`

export function formatEliteOfferValidTillLabel(offerValidTill?: number | null): string | undefined {
  if (!offerValidTill) return 'A special perk just for you'

  const expiryTimestampMs = toOfferTimestampMs(offerValidTill)
  if (expiryTimestampMs == null) return 'A special perk just for you'

  const expiryDate = new Date(expiryTimestampMs)
  const today = new Date()
  const expiryDateOnly = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate())
  const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  if (expiryDateOnly.getTime() === todayDateOnly.getTime()) {
    return 'Offer ends at 11:59PM tonight'
  }

  const formattedDate = convertEpochToDate(expiryTimestampMs, '%d %b')
  const year = expiryDate.getFullYear().toString().slice(-2)
  return `Offer valid only till ${formattedDate} '${year}`
}

export function formatSignupTenureOfferValidTillLabel(offerValidTill?: number | null): string | undefined {
  if (isOfferLessThanOneDayRemaining(offerValidTill)) return undefined

  const expiryTimestampMs = toOfferTimestampMs(offerValidTill)
  if (expiryTimestampMs == null) return 'A special perk just for you'

  const expiryDate = new Date(expiryTimestampMs)
  const today = new Date()
  const expiryDateOnly = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate())
  const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const tomorrowDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)

  if (expiryDateOnly.getTime() === tomorrowDateOnly.getTime()) {
    return 'Offer valid till tomorrow'
  }

  if (expiryDateOnly.getTime() === todayDateOnly.getTime()) {
    return 'Offer ends at 11:59PM tonight'
  }

  const formattedDate = convertEpochToDate(expiryTimestampMs, '%d %b')
  const year = expiryDate.getFullYear().toString().slice(-2)
  return `Offer valid only till ${formattedDate} '${year}`
}

export function getTenureBasedGiftBannerCopy(baseMonths: number, extraMonths: number): string {
  return `Get ${baseMonths} + ${extraMonths} ${extraMonths === 1 ? 'month' : 'months'} extra for free`
}

export function getSignupTenureGiftBannerCopy(extraMonths: number): string {
  return `Get ${extraMonths} extra ${extraMonths === 1 ? 'month' : 'months'} of Elite, FREE`
}

export function resolveTenureBasedBaseMonths(
  baseTenure: { value?: number | string; period?: string } | undefined
): number {
  const value = Number(baseTenure?.value ?? 12)
  if (baseTenure?.period === 'YEAR') return value * 12
  if (baseTenure?.period === 'MONTH') return value
  return 12
}

export function EliteSpecialOfferGiftBanner({
  variant,
  offerType,
  signupOffer,
  offerValidTill,
  baseMonths = 12,
  extraMonths = 2,
  className = '',
  enableShine = true,
  isSelected = true,
  onClick
}: {
  variant: EliteSpecialOfferGiftBannerVariant
  offerType?: ElitePlanOfferType | null
  signupOffer?: boolean | null
  offerValidTill?: number | null
  baseMonths?: number
  extraMonths?: number
  className?: string
  enableShine?: boolean
  isSelected?: boolean
  onClick?: () => void
}) {
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const [countdownLabel, setCountdownLabel] = useState<string | undefined>(undefined)
  const bannerRef = useRef<HTMLDivElement>(null)
  const isSignupTenureOffer = isSignupTenureBasedOffer(offerType, signupOffer)
  const showLiveCountdown =
    isSignupTenureOffer && isOfferLessThanOneDayRemaining(offerValidTill)

  useEffect(() => {
    if (!showLiveCountdown) {
      setCountdownLabel(undefined)
      return
    }

    const updateCountdown = () => {
      const remainingMs = getOfferRemainingMs(offerValidTill)
      if (remainingMs <= 0) {
        setCountdownLabel('Offer ended')
        return
      }
      setCountdownLabel(`Offer ends in ${formatOfferCountdown(remainingMs)}`)
    }

    updateCountdown()
    const intervalId = window.setInterval(updateCountdown, 1000)
    return () => window.clearInterval(intervalId)
  }, [showLiveCountdown, offerValidTill])

  const expiryText = (() => {
    if (isSignupTenureOffer) {
      if (showLiveCountdown) return countdownLabel
      return formatSignupTenureOfferValidTillLabel(offerValidTill)
    }
    return formatEliteOfferValidTillLabel(offerValidTill)
  })()

  const headline = (() => {
    if (isTwelvePlusThreeOffer(offerType)) {
      return getSignupTenureGiftBannerCopy(TWELVE_PLUS_THREE_EXTRA_MONTHS)
    }
    if (isSignupTenureOffer) return getSignupTenureGiftBannerCopy(extraMonths)
    if (variant === 'TENURE_BASED') return getTenureBasedGiftBannerCopy(baseMonths, extraMonths)
    return PRICE_BASED_COPY
  })()

  useEffect(() => {
    if (!enableShine) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setShouldAnimate(true)
          }, 500)
        }
      },
      { threshold: 0.1 }
    )

    if (bannerRef.current) {
      observer.observe(bannerRef.current)
    }

    return () => observer.disconnect()
  }, [enableShine])

  const bannerImage = resolveSpecialOfferGiftBannerImage(offerType, variant, signupOffer)

  return (
    <div
      ref={bannerRef}
      onClick={onClick}
      className={`relative overflow-hidden px-4 py-[10px] text-left ${isSelected ? 'text-white' : 'text-[#65321D]'} ${onClick ? 'cursor-pointer' : ''} ${className}`}
    >
      {enableShine ? (
        <div
          className='pointer-events-none absolute top-0 left-7 z-[1] h-full w-full scale-125'
          style={{
            animation: shouldAnimate ? 'elite-special-offer-shine 1.2s ease-out forwards' : 'none',
            transform: 'translateX(-100%) scale(1.25)',
            opacity: 1
          }}
        >
          <RenderHTML innerHTMLText={shineIconSvg} />
        </div>
      ) : null}
      <div className='relative z-10 pr-24'>
        <p className='text-sm font-medium leading-[1.3]'>{headline}</p>
        {expiryText ? <p className='text-13 mt-0.5 leading-[1.3] opacity-80'>{expiryText}</p> : null}
      </div>
      <div className='pointer-events-none absolute top-0 right-0 z-10 flex h-full items-center'>
        <img
          src={bannerImage}
          alt=''
          className='h-full max-h-[72px] object-contain'
        />
      </div>
      {enableShine ? <style dangerouslySetInnerHTML={{ __html: shineKeyframesCss }} /> : null}
    </div>
  )
}
